import { motion } from "framer-motion";
import { Heart, Plus, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import { useStore } from "@/context/StoreContext";
import { inr, offerLabel, FALLBACK_IMG } from "@/lib/api";

export default function ProductCard({ p, wide = false }) {
  const { addToCart, toggleWishlist, wishlist, requireAuth, cartData } = useStore();
  const isSaved = wishlist.ids.includes(p.id);
  const out = p.inStock === false || p.stock === 0;
  const inBagQty = (cartData?.cart?.items || []).find((it) => it.product_id === p.id)?.qty || 0;

  return (
    <motion.article
      data-testid={`product-card-${p.id}`}
      whileHover={{ y: -6 }}
      transition={{ type: "spring", stiffness: 300, damping: 22 }}
      className={wide ? "group w-full" : "group w-[220px] shrink-0 snap-start sm:w-[250px]"}
    >
      <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-champagne/40 bg-moss">
        <Link to={`/product/${p.slug || p.id}`} data-testid={`product-link-${p.id}`} aria-label={p.name}>
          <img src={p.img} alt={p.name} loading="lazy" onError={(e) => { if (!e.currentTarget.dataset.fbk) { e.currentTarget.dataset.fbk = "1"; e.currentTarget.src = FALLBACK_IMG; } }} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" />
        </Link>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-forest/60 via-transparent to-transparent" />
        {p.offer ? (
          <span data-testid={`offer-badge-${p.id}`} className="absolute left-3 top-3 rounded-full bg-black px-2.5 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.15em] text-white">{offerLabel(p.offer)}</span>
        ) : p.off > 0 && (
          <span className="absolute left-3 top-3 rounded-full bg-black px-2.5 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.15em] text-white">Offer {p.off}%</span>
        )}
        <button
          data-testid={`wishlist-toggle-${p.id}`}
          onClick={() => requireAuth(() => toggleWishlist(p.id))}
          className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/70 backdrop-blur-md transition-colors hover:bg-white"
          aria-label="Toggle wishlist"
        >
          <Heart className={`h-4 w-4 transition-colors ${isSaved ? "fill-gold text-gold" : "text-cream"}`} />
        </button>
        {out ? (
          <span data-testid={`out-of-stock-${p.id}`} className="absolute inset-x-3 bottom-3 rounded-full bg-cream/90 py-2.5 text-center text-[11px] font-bold uppercase tracking-[0.15em] text-forest">Out of Stock</span>
        ) : (
          <button
            data-testid={`add-to-cart-${p.id}`}
            onClick={() => requireAuth(() => addToCart(p.id, 1))}
            className={`absolute inset-x-3 bottom-3 flex items-center justify-center gap-2 rounded-full py-2.5 text-[11px] font-bold uppercase tracking-[0.15em] transition-all duration-500 ${inBagQty > 0 ? "bg-gold text-forest hover:bg-cream" : "bg-cream text-forest hover:bg-gold md:translate-y-[140%] md:opacity-0 md:group-hover:translate-y-0 md:group-hover:opacity-100"}`}
          >
            {inBagQty > 0 ? (
              <><ShoppingBag className="h-3.5 w-3.5" /> In Bag × {inBagQty} · Add more</>
            ) : (
              <><Plus className="h-3.5 w-3.5" /> Add to Bag</>
            )}
          </button>
        )}
      </div>
      <div className="mt-3 space-y-1 px-1">
        {p.brand && <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-gold">{p.brand}</p>}
        <Link to={`/product/${p.slug || p.id}`}>
          <h3 className="line-clamp-1 font-display text-base leading-snug text-cream transition-colors hover:text-gold">{p.name}</h3>
        </Link>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-0.5">
          {p.listPrice > p.price && (
            <span className="text-xs text-fern line-through">{inr(p.listPrice)}</span>
          )}
          <span className="rounded-md bg-gold/15 px-2 py-0.5 font-display text-base font-bold text-gold" data-testid={`offer-price-${p.id}`}>{inr(p.price)}</span>
          {p.off > 0 && <span className="rounded bg-black px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-white">Offer {p.off}%</span>}
        </div>
        {p.mrp > 0 && <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-fern">MRP {inr(p.mrp)}</p>}
      </div>
    </motion.article>
  );
}
